import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

/**
 * Spec §7: three languages, Pashto/Dari are RTL. (Urdu was dropped.)
 *
 * These are the UI languages. They are intentionally the same three codes as
 * the audio Language enum, but they are not the same axis: a school can read
 * the UI in Pashto while watching the English audio track.
 */
export const UI_LANGS = {
  EN: { label: 'English', dir: 'ltr' },
  PS: { label: 'پښتو', dir: 'rtl' },
  DA: { label: 'دری', dir: 'rtl' },
} as const;

export type UiLang = keyof typeof UI_LANGS;

const en = {
  appName: 'EasyLearning Books',
  login: {
    title: 'Sign in',
    email: 'Email',
    password: 'Password',
    submit: 'Sign in',
    signingIn: 'Signing in…',
    failed: 'Sign in failed. Check your email and password.',
    captchaFailed: "The security check didn't pass. Please try again.",
    locked: 'This account is temporarily locked. Try again in 15 minutes.',
    accountSuspended: 'This account is suspended. Contact your school administrator.',
    schoolSuspended: "This school's access is suspended. Contact your school administrator.",
    schoolExpired: "This school's access has ended. Contact your school administrator.",
    noSchool: 'This account is not linked to a school. Contact your school administrator.',
    forgot: 'Forgot password?',
  },
  forgot: {
    title: 'Reset your password',
    intro: "Enter your email address and we'll send you a link to set a new password.",
    email: 'Email',
    submit: 'Send reset link',
    sending: 'Sending…',
    backToLogin: 'Back to sign in',
    // Shown whether or not the address is registered — the server answers the
    // same either way, and saying "no such account" here would undo that.
    sentTitle: 'Check your inbox',
    sent: "If an account exists for that email, we've sent a reset link. It expires in 1 hour.",
    captchaFailed: "The security check didn't pass. Please try again.",
    rateLimited: 'Too many requests. Please wait a minute and try again.',
    failed: 'Something went wrong. Please try again.',
  },
  reset: {
    title: 'Set a new password',
    password: 'New password',
    confirm: 'Confirm new password',
    hint: 'At least 8 characters.',
    submit: 'Set new password',
    saving: 'Saving…',
    mismatch: 'The two passwords do not match.',
    tooShort: 'Password must be at least 8 characters.',
    doneTitle: 'Password updated',
    done: 'Your password has been changed. You can now sign in with it.',
    toLogin: 'Go to sign in',
    invalid: 'This reset link is invalid or has expired.',
    requestNew: 'Request a new link',
    missingToken: 'This page needs a reset link from your email.',
    rateLimited: 'Too many attempts. Please wait a minute and try again.',
    failed: 'Something went wrong. Please try again.',
  },
  nav: {
    home: 'Grades',
    signOut: 'Sign out',
    admin: 'Admin',
    usage: 'Usage',
    school: 'School',
    coordinator: 'My teachers',
    account: 'Account',
  },
  account: {
    title: 'Account settings',
    // -- change password card
    passwordTitle: 'Change password',
    current: 'Current password',
    newPassword: 'New password',
    confirm: 'Confirm new password',
    hint: 'At least 8 characters.',
    save: 'Change password',
    saving: 'Saving…',
    passwordDone: 'Your password has been changed.',
    signedOutElsewhere: 'Any other devices signed in to this account have been signed out.',
    mismatch: 'The two passwords do not match.',
    tooShort: 'Password must be at least 8 characters.',
    // -- change email card
    emailTitle: 'Change login email',
    currentEmail: 'Current login email',
    newEmail: 'New login email',
    saveEmail: 'Change email',
    emailDone: 'Your login email has been changed. Use it the next time you sign in.',
    emailNotice: "We've emailed both your old and new addresses to confirm the change.",
    // -- refusals, keyed by the server's `code`
    wrongPassword: 'Current password is incorrect.',
    passwordUnchanged: 'The new password must be different from the current one.',
    emailUnchanged: 'That is already your login email.',
    emailTaken: 'That email is already in use.',
    locked: 'Too many incorrect attempts. Try again in 15 minutes.',
    failed: 'Something went wrong. Please try again.',
    // -- shown only if re-establishing the session fails after a change
    sessionLost: 'Your details were changed. Please sign in again with your new details.',
  },
  grades: { title: 'Grades', subjects_one: '{{count}} subject', subjects_other: '{{count}} subjects' },
  subjects: { title: 'Subjects', books_one: '{{count}} book', books_other: '{{count}} books' },
  book: {
    lessons: 'Lessons',
    videos_one: '{{count}} lesson',
    videos_other: '{{count}} lessons',
    downloadAll: 'Download all (SD)',
    downloadingAll: 'Downloading {{done}} of {{total}}…',
    cancel: 'Cancel',
  },
  watch: {
    audio: 'Audio language',
    quality: 'Quality',
    download: 'Download',
    preparing: 'Preparing…',
    noVariant: 'This lesson is not available in that combination yet.',
    playbackError: 'Playback failed. Reloading the lesson…',
  },
  common: { loading: 'Loading…', error: 'Something went wrong.', retry: 'Retry', back: 'Back' },
};

/**
 * PS/DA ship as English placeholders so the RTL layout is exercised from
 * day one rather than discovered at pilot time. Replace with real strings —
 * the keys are stable.
 */
const placeholder = (lang: string) => ({
  ...en,
  appName: `EasyLearning Books (${lang})`,
});

void i18n.use(initReactI18next).init({
  resources: {
    EN: { translation: en },
    PS: { translation: placeholder('PS') },
    DA: { translation: placeholder('DA') },
  },
  lng: (localStorage.getItem('uiLang') as UiLang) ?? 'EN',
  fallbackLng: 'EN',
  interpolation: { escapeValue: false },
});

export function applyDir(lang: UiLang) {
  const dir = UI_LANGS[lang].dir;
  document.documentElement.dir = dir;
  document.documentElement.lang = lang.toLowerCase();
}

applyDir((localStorage.getItem('uiLang') as UiLang) ?? 'EN');

export default i18n;
